import React from 'react';
import Modal from 'react-bootstrap/Modal';
import { OrderForListing } from '../../models/OrderForListing';
import { FileFormat } from '../../enums/FileFormat';
import { strings } from '../../resources/strings';
import { PdfDownloadHelper } from '../../helpers/PdfDownloadHelper';
import { PdfConcatHelper } from '../../helpers/PdfConcatHelper';
import { usePrintListOrders } from '../../hooks/usePrintListOrders';
import ServicesContext from '../../contexts/ServicesContext';
import { CarrierDanfeOrderLabelForPreGenerationInfo, GetDanfeLabelPreGenerationInfoOutput } from '../../services/Labels/GetDanfeLabelPreGenerationInfo/GetDanfeLabelPreGenerationInfoService';
import { Button } from '../../common-components/Button/Button';
import { FooterContent, PreGenerationOptionsContainer } from './styles';

interface IDanfeLabelPreGenerationModalProps {
  close: (orderId?: number) => void;
  ordersForListing: OrderForListing[];
  fileFormat: FileFormat;
}

interface IConfirmPreGenerationOption {
  text: string;
  actionName?: string;
  click: () => void;
}

interface IOperationStatus {
  exec: boolean;
  completed: boolean;
}

// steps
const stepIds = {
  loadingPreGenerationInfo: 'loading-pre-generation-info',
  confirmPreGeneration: 'confirm-pre-generation',
  getting: 'getting',
  generation: 'generation',
};
const steps = [
  {
    id: stepIds.loadingPreGenerationInfo,
    title: 'Aguarde, carregando informações para geração de etiquetas...',
  },
  {
    id: stepIds.confirmPreGeneration,
    title: 'Atenção!',
  },
  {
    id: stepIds.getting,
    title: 'Aguarde, obtendo etiquetas necessárias...',
  },
  {
    id: stepIds.generation,
    title: 'Aguarde, gerando etiquetas para impressão...',
  }
];

const DanfeLabelPreGenerationModal: React.FC<IDanfeLabelPreGenerationModalProps> = ({
  close,
  ordersForListing,
  fileFormat
}) => {

  // state para step
  const [step, setStep] = React.useState<string>(steps[0].id);
  // state para preGenerationInfo
  const [preGenerationInfo, setPreGenerationInfo] = React.useState<GetDanfeLabelPreGenerationInfoOutput | null>(null);
  // state para detalhar progresso de obtenção de etiquetas de transportadora
  const [carrierLabelProgress, setCarrierLabelProgress] = React.useState<{
    progress: number;
    subtitle: string;
    errorCount: number;
  } | null>(null);
  // state para salvar todos pedidos+label obtidos de transportadora
  const [ordersWithLabelsPreGeneratedByCarrier, setOrdersWithLabelsPreGeneratedByCarrier] = React.useState<CarrierDanfeOrderLabelForPreGenerationInfo[]>([]);
  // state de opções de confirmação
  const [confirmPreGenerationOptions, setConfirmPreGenerationOptions] = React.useState<IConfirmPreGenerationOption[]>([]);
  // states de info sobre operações de geração de etiquetas
  const [printZplDanfeLabelStatus, setPrintZplDanfeLabelStatus] = React.useState<IOperationStatus>({ exec: false, completed: false });
  const [printCarrierLabelStatus, setPrintCarrierLabelStatus] = React.useState<IOperationStatus>({ exec: false, completed: false });
  const [getCarrierLabelStatus, setGetCarrierLabelStatus] = React.useState<IOperationStatus>({ exec: false, completed: false });

  const {
    printZplDanfePackingLabel,
  } = usePrintListOrders();

  // services from ServicesContext
  const {
    getDanfeLabelPreGenerationInfoService,
    getCarrierDanfeLabelByOrdersService,
    reportService,
  } = React.useContext(ServicesContext);

  // use effects
  React.useEffect(() => {
    getPreGenerationInfo();
  }, []);

  const generationProgressValues = React.useMemo(() => {
    let operationsInExecution = 0;
    if (printZplDanfeLabelStatus.exec) operationsInExecution++;
    if (printCarrierLabelStatus.exec) operationsInExecution++;
    if (getCarrierLabelStatus.exec) operationsInExecution++;
    let completedCount = 0;
    if (printZplDanfeLabelStatus.completed) completedCount++;
    if (printCarrierLabelStatus.completed) completedCount++;
    if (getCarrierLabelStatus.completed) completedCount++;

    const isInExecution = operationsInExecution > 0;
    const isCompleted = isInExecution && completedCount === operationsInExecution;

    return {
      isInExecution,
      isCompleted,
    }
  }, [printZplDanfeLabelStatus, printCarrierLabelStatus, getCarrierLabelStatus]);

  const generationProgressCard = React.useMemo(() => {
    if (!generationProgressValues.isInExecution) return null;
    if (!generationProgressValues.isCompleted) {
      return (
        <div>
          Aguarde, gerando etiqueta para impressão...
        </div>
      )
    }
    return (
      <div style={{ width: '100%', textAlign: 'center' }}>
        Pronto!
      </div>
    )
  }, [generationProgressValues]);

  return (
    <Modal
      show={true}
      onHide={clickCloseModal}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton style={{ height: '50px' }}>
        <Modal.Title>Gerar Etiquetas</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {step == stepIds.loadingPreGenerationInfo && (
          <p>{steps[0].title}</p>
        )}
        {step == stepIds.confirmPreGeneration && (
          <>
            <p><strong>{steps[1].title}</strong> {preGenerationInfo?.message}</p>
            <p>Escolha como prosseguir:</p>
            <PreGenerationOptionsContainer>
              {confirmPreGenerationOptions.map((option, index) => (
                <Button key={index} text={option.text} click={option.click}
                  marginBottom='10px' actionName={option.actionName} />
              ))}
            </PreGenerationOptionsContainer>
          </>
        )}
        {step == stepIds.getting && (
          <>
            <p>{steps[2].title}</p>
            {carrierLabelProgress?.subtitle && (
              <p>{carrierLabelProgress?.subtitle}</p>
            )}
          </>
        )}
        {step == stepIds.generation && (
          <>
            {generationProgressCard}
          </>
        )}
      </Modal.Body>

      {step == stepIds.loadingPreGenerationInfo || step == stepIds.getting || step == stepIds.generation ? (
        <Modal.Footer>
          <FooterContent>
            {step == stepIds.generation ? 
              generationProgressValues.isInExecution && !generationProgressValues.isCompleted ? (
                <Button text={strings.Cancel} width='100px' click={clickCloseModal} />
              ) : generationProgressValues.isInExecution && generationProgressValues.isCompleted ? (
                <Button text={strings.Ok} width='100px' click={clickCloseModal} />
              ) : null
            : null}
            {step !== stepIds.generation && (
              <Button text={strings.Cancel} width='100px' click={clickCloseModal} />
            )}
          </FooterContent>
        </Modal.Footer>
      ) : null}
    </Modal>
  );

  function clickCloseModal() {
    close();
  }

  function getPreGenerationInfo() {
    console.log('getting pre generation info...');

    // limpar states
    setStep(stepIds.loadingPreGenerationInfo);
    setPreGenerationInfo(null);
    setCarrierLabelProgress(null);
    setOrdersWithLabelsPreGeneratedByCarrier([]);
    setConfirmPreGenerationOptions([]);
    setPrintZplDanfeLabelStatus({ exec: false, completed: false });
    setPrintCarrierLabelStatus({ exec: false, completed: false });
    setGetCarrierLabelStatus({ exec: false, completed: false });

    handleGetPreGenerationInfo();
  }

  function handleGetPreGenerationInfo() {
    getDanfeLabelPreGenerationInfoService
      .handle({ orderIds: ordersForListing.map(orderForListing => orderForListing.order.id)})
      .then(result => {
        const preGenerationInfoResult = result?.value || null;
        console.log('pre generation info result:', preGenerationInfoResult);
        setPreGenerationInfo(preGenerationInfoResult);

        // se tem mensagem, tem que mostrar as opções

        const options: IConfirmPreGenerationOption[] = [];

        // quantidade de etiquetas danfe
        const danfeLabelCount = preGenerationInfoResult?.orderIdsNotGeneratingLabelsByCarrier?.length || 0;
        // quantidade de etiquetas de transportadora para obter
        const pendingCarrierLabelCount = preGenerationInfoResult?.orderIdsPendingLabelPreGenerationByCarrier?.length || 0;
        // quantidade de etiquetas de transportadora prontas
        const carrierLabelCount = preGenerationInfoResult?.ordersWithLabelsPreGeneratedByCarrier?.length || 0;

        console.log('danfe label count:', danfeLabelCount);
        console.log('pending carrier label count:', pendingCarrierLabelCount);
        console.log('carrier label count:', carrierLabelCount);

        // só tem etiqueta danfe, não tem etiqueta de transportadora para obter e também não tem etiqueta de transportadora pronta
        if (danfeLabelCount > 0 && !pendingCarrierLabelCount && !carrierLabelCount) {
          options.push({
            text: 'Baixar etiquetas danfe',
            actionName: 'handlePrintZplDanfeLabel',
            click: () => {
              handlePrintZplDanfeLabel();
            }
          });
        }

        // não tem etiqueta danfe, não tem etiqueta de transportadora para obter e tem etiqueta de transportadora pronta
        if (!danfeLabelCount && !pendingCarrierLabelCount && carrierLabelCount > 0) {
          options.push({
            text: 'Baixar etiquetas de transportadora',
            actionName: 'downloadCarrierLabels',
            click: () => {
              downloadCarrierLabels(preGenerationInfoResult);
            }
          });
        }

        // não tem etiqueta danfe, tem etiqueta de transportadora para obter e tem etiqueta de transportadora pronta
        if (!danfeLabelCount && pendingCarrierLabelCount > 0 && carrierLabelCount > 0) {
          options.push({
            text: 'Baixar tudo',
            actionName: 'getCarrierLabels',
            click: () => {
              getCarrierLabels(preGenerationInfoResult);
            }
          });
          options.push({
            text: 'Baixar etiquetas de transportadora',
            actionName: 'downloadCarrierLabels',
            click: () => {
              downloadCarrierLabels(preGenerationInfoResult);
            }
          });
        }

        // tem etiqueta danfe, tem etiqueta de transportadora para obter e tem etiqueta de transportadora pronta
        if (danfeLabelCount > 0 && pendingCarrierLabelCount > 0 && carrierLabelCount > 0) {
          options.push({
            text: 'Baixar tudo',
            actionName: 'getCarrierLabels',
            click: () => {
              getCarrierLabels();
              setTimeout(() => {
                handlePrintZplDanfeLabel();
              }, 2000);
            }
          });
          options.push({
            text: 'Baixar etiquetas de transportadora',
            actionName: 'downloadCarrierLabels',
            click: () => {
              downloadCarrierLabels(preGenerationInfoResult);
            }
          });
          options.push({
            text: 'Baixar etiquetas danfe',
            actionName: 'handlePrintZplDanfeLabel',
            click: () => {
              handlePrintZplDanfeLabel();
            }
          });
        }

        // tem etiqueta danfe, não tem etiqueta de transportadora para obter e tem etiqueta de transportadora pronta
        if (danfeLabelCount > 0 && !pendingCarrierLabelCount && carrierLabelCount > 0) {
          options.push({
            text: 'Baixar tudo',
            actionName: 'getCarrierLabels',
            click: () => {
              downloadCarrierLabels(preGenerationInfoResult);
              setTimeout(() => {
                handlePrintZplDanfeLabel();
              }, 2000);
            }
          });
          options.push({
            text: 'Baixar etiquetas de transportadora',
            actionName: 'downloadCarrierLabels',
            click: () => {
              downloadCarrierLabels(preGenerationInfoResult);
            }
          });
          options.push({
            text: 'Baixar etiquetas danfe',
            actionName: 'handlePrintZplDanfeLabel',
            click: () => {
              handlePrintZplDanfeLabel();
            }
          });
        }

        // tem etiqueta danfe, tem etiqueta de transportadora para obter e não tem etiqueta de transportadora pronta
        if (danfeLabelCount > 0 && pendingCarrierLabelCount > 0 && !carrierLabelCount) {
          options.push({
            text: 'Baixar tudo',
            actionName: 'getCarrierLabels',
            click: () => {
              getCarrierLabels(preGenerationInfoResult);
              setTimeout(() => {
                handlePrintZplDanfeLabel();
              }, 2000);
            }
          });
          options.push({
            text: 'Baixar etiquetas de transportadora',
            actionName: 'getCarrierLabels',
            click: () => {
              getCarrierLabels(preGenerationInfoResult);
            }
          });
        }

        // não tem etiqueta danfe, tem etiqueta de transportadora para obter e não tem etiqueta de transportadora pronta
        if (!danfeLabelCount && pendingCarrierLabelCount > 0 && !carrierLabelCount) {
          options.push({
            text: 'Baixar etiquetas de transportadora', 
            actionName: 'getCarrierLabels',
            click: () => {
              getCarrierLabels(preGenerationInfoResult);
            }
          });
        }

        setConfirmPreGenerationOptions(options);

        if (preGenerationInfoResult?.message) {
          setStep(stepIds.confirmPreGeneration);
          return;
        }

        if (!options.length) {
          return;
        }

        options[0].click();
      });
  }

  // função para iniciar processo de obtenção de etiquetas de transportadora
  function getCarrierLabels(preGenerationInfoResult?: GetDanfeLabelPreGenerationInfoOutput | null) {
    const preInfo = preGenerationInfoResult || preGenerationInfo;
    console.log('get carrier labels... pre generation info:', preInfo);

    setStep(stepIds.getting);
    setCarrierLabelProgress({
      progress: 0,
      subtitle: 'Obtendo etiquetas de transportadora... 0%',
      errorCount: 0
    });
    getCarrierLabelForOrder(0, preInfo);
  }

  // função para obter etiquetas de transportadora para um pedido de cada vez até que todas sejam obtidas
  function getCarrierLabelForOrder(index: number, preGenerationInfoResult?: GetDanfeLabelPreGenerationInfoOutput | null) {
    const preInfo = preGenerationInfoResult || preGenerationInfo;
    setStep(stepIds.getting);
    setGetCarrierLabelStatus({ exec: true, completed: false });
    const orderIdsPendingLabelPreGenerationByCarrierLength = preInfo?.orderIdsPendingLabelPreGenerationByCarrier?.length || 0;
    console.log(`index: ${index} >= orderIdsPendingLabelPreGenerationByCarrierLength: ${orderIdsPendingLabelPreGenerationByCarrierLength}`);
    if (index >= orderIdsPendingLabelPreGenerationByCarrierLength) {
      setGetCarrierLabelStatus({ exec: true, completed: true });
      downloadCarrierLabels(preInfo);
      return;
    }
    const progress = orderIdsPendingLabelPreGenerationByCarrierLength > 0
      ? Math.round((index + 1) / orderIdsPendingLabelPreGenerationByCarrierLength * 100)
      : 0;
    const subtitle = `Obtendo etiquetas de transportadora... ${progress}%`;
    const orderId = preInfo && preInfo.orderIdsPendingLabelPreGenerationByCarrier
        ? preInfo.orderIdsPendingLabelPreGenerationByCarrier[index]
        : 0;
    if (!orderId) {
      setGetCarrierLabelStatus({ exec: true, completed: true });
      downloadCarrierLabels(preInfo);
      return;
    }
    getCarrierDanfeLabelByOrdersService.handle({
      orderIds: [orderId]
    }).then(result => {
      const label = result?.value?.orderLabels?.[0]?.label || null;
      if (label) {
        setOrdersWithLabelsPreGeneratedByCarrier([
          ...ordersWithLabelsPreGeneratedByCarrier,
          {
            orderId,
            label
          }
        ]);
        setCarrierLabelProgress({
          progress,
          subtitle,
          errorCount: carrierLabelProgress?.errorCount || 0
        });
      } else {
        setCarrierLabelProgress({
          progress,
          subtitle,
          errorCount: (carrierLabelProgress?.errorCount || 0) + 1
        });
      }
      // aguardar 1 segundo para não sobrecarregar o servidor
      setTimeout(() => {
        getCarrierLabelForOrder(index + 1, preInfo);
      }, 1000);
    });
  }

  // função para gerar etiquetas de transportadora
  async function downloadCarrierLabels(preGenerationInfoResult?: GetDanfeLabelPreGenerationInfoOutput | null) {
    console.log('download carrier labels...');

    setStep(stepIds.generation);
    setPrintCarrierLabelStatus({ exec: true, completed: false });

    const allOrdersWithLabelsPreGeneratedByCarrier = [];
    if (ordersWithLabelsPreGeneratedByCarrier && ordersWithLabelsPreGeneratedByCarrier.length) {
      allOrdersWithLabelsPreGeneratedByCarrier.push(...ordersWithLabelsPreGeneratedByCarrier);
    }
    const preInfo = preGenerationInfoResult || preGenerationInfo;
    if (preInfo && preInfo.ordersWithLabelsPreGeneratedByCarrier && preInfo.ordersWithLabelsPreGeneratedByCarrier.length) {
      allOrdersWithLabelsPreGeneratedByCarrier.push(...preInfo.ordersWithLabelsPreGeneratedByCarrier);
    }
    const paths = allOrdersWithLabelsPreGeneratedByCarrier.map(orderWithLabelPreGeneratedByCarrier => (
      orderWithLabelPreGeneratedByCarrier.label
    ));
    const pdfs = await PdfDownloadHelper.fetchPdfs(paths);
    const concatenatedPdf = await PdfConcatHelper.concat(pdfs.map(pdf => pdf.base64 || ''));
    reportService.downloadPdf(concatenatedPdf, 'etiquetas');

    setPrintCarrierLabelStatus({ exec: true, completed: true });
  }

  function handlePrintZplDanfeLabel() {
    console.log('handle print ppl danfe...');

    setStep(stepIds.generation);
    setPrintZplDanfeLabelStatus({ exec: true, completed: false });

    printZplDanfePackingLabel(ordersForListing);
    
    setPrintZplDanfeLabelStatus({ exec: true, completed: true });
  }

};

export { DanfeLabelPreGenerationModal };